import React from 'react';
import Navbar from './Navbar';
import '../styles.css';

function Pricing() {
  return (
    <div className="font-sans text-slate-800 bg-slate-50 min-h-screen flex flex-col">
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200/60 shadow-sm/50">
        <Navbar />
      </div>

      <main className="flex-1 w-full flex flex-col items-center py-20 px-6">
        <div className="max-w-6xl w-full text-center mb-16">
          <h1 className="text-4xl lg:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
            Stop Overpaying Property Taxes
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Get your instant diagnosis for free, then unlock the full data-backed evidence needed to win your appeal.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 w-full max-w-6xl">
          
          {/* Single Report Tier */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 flex flex-col h-full transition-transform hover:-translate-y-1">
            <div className="mb-8">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Single Report</h3>
              <p className="text-slate-500 text-sm italic">Perfect for a single property appeal.</p>
            </div>
            <div className="mb-8">
              <span className="text-4xl font-black text-slate-900">$39</span>
              <span className="text-slate-500 ml-2">/one-time</span>
            </div>
            <ul className="space-y-4 mb-10 flex-1">
              <li className="flex items-center gap-3 text-slate-600">
                <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                1 Report Credit (Never Expires)
              </li>
              <li className="flex items-center gap-3 text-slate-600">
                <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                Full Comparable Sales Analysis
              </li>
              <li className="flex items-center gap-3 text-slate-600">
                <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                Editable Word Doc Appeal Report (.doc)
              </li>
              <li className="flex items-center gap-3 text-slate-600">
                <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                Market Trend & Volume Charts
              </li>
            </ul>
            <a href="/payment?plan=single" className="w-full py-4 px-6 rounded-full border-2 border-sky-500 text-sky-600 font-bold hover:bg-sky-50 transition-colors text-center">
              Buy 1 Report ($39)
            </a>
          </div>

          {/* 5-Report Pack (Best Value) */}
          <div className="bg-slate-900 rounded-3xl shadow-2xl border border-slate-800 p-8 flex flex-col h-full relative transform scale-105 ring-4 ring-sky-500/20">
            <div className="absolute top-0 right-8 -translate-y-1/2 bg-sky-500 text-white text-xs font-black px-4 py-1.5 rounded-full uppercase tracking-widest shadow-lg">Best Value (Save 60%)</div>
            <div className="mb-8">
              <h3 className="text-xl font-bold text-white mb-2">5-Report Pack</h3>
              <p className="text-slate-400 text-sm italic">For multi-property owners, agents & investors.</p>
            </div>
            <div className="mb-8">
              <span className="text-4xl font-black text-white">$79</span>
              <span className="text-slate-400 ml-2">/one-time ($15.80/report)</span>
            </div>
            <ul className="space-y-4 mb-10 flex-1">
              <li className="flex items-center gap-3 text-white font-medium">
                <svg className="w-5 h-5 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                5 Report Credits (Never Expire!)
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <svg className="w-5 h-5 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                Detailed Comparable Sales (Up to 50 Comps)
              </li>
              <li className="flex items-center gap-3 text-white font-medium">
                <svg className="w-5 h-5 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                Editable Word Doc Appeal Reports (.doc)
              </li>
              <li className="flex items-center gap-3 text-white font-medium">
                <svg className="w-5 h-5 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                Market Trend & Volume Charts
              </li>
            </ul>
            <a href="/payment?plan=pack5" className="w-full py-4 px-6 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 transition-all shadow-lg shadow-sky-500/25 text-center">
              Buy 5-Report Pack ($79)
            </a>
          </div>

          {/* Professional Tier */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 flex flex-col h-full transition-transform hover:-translate-y-1">
            <div className="mb-8">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Professional</h3>
              <p className="text-slate-500 text-sm italic">For Tax Consultants & High Volume.</p>
            </div>
            <div className="mb-8">
              <span className="text-3xl font-extrabold text-slate-900">Custom</span>
            </div>
            <ul className="space-y-4 mb-10 flex-1">
              <li className="flex items-center gap-3 text-slate-600">
                <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                Bulk Property Reports
              </li>
              <li className="flex items-center gap-3 text-slate-600">
                <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                White-Labeled Branding
              </li>
              <li className="flex items-center gap-3 text-slate-600">
                <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                Client Lead Generator Tool
              </li>
              <li className="flex items-center gap-3 text-slate-600">
                <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                Priority API Support
              </li>
            </ul>
            <a href="/contact" className="w-full py-4 px-6 rounded-full border-2 border-slate-900 text-slate-900 font-bold hover:bg-slate-900 hover:text-white transition-all text-center">
              Contact Sales
            </a>
          </div>

        </div>

        <section className="mt-24 max-w-4xl text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8 text-left">
            <div>
              <h4 className="font-bold text-slate-900 mb-2">How accurate is the valuation?</h4>
              <p className="text-slate-600 text-sm">We use data from the same sources as major financial institutions and real estate platforms, updated daily with fresh sales and property records.</p>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-2">Do you guarantee my taxes will go down?</h4>
              <p className="text-slate-600 text-sm">We provide the data and professional-grade reports to prove your home is worth less than its assessment. While we can't control the final decision of tax boards, we give you the best possible evidence to win.</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="w-full bg-slate-100 py-12 border-t border-slate-200 mt-auto">
        <div className="max-w-6xl mx-auto px-6 text-center text-slate-500">
          <p>© {new Date().getFullYear()} Casalytix. All rights reserved. | <a href="/privacy-policy" className="hover:text-sky-500 transition-colors">Privacy Policy</a></p>
        </div>
      </footer>
    </div>
  );
}

export default Pricing;